import { useState, useEffect, useCallback, useMemo } from 'react'
import * as LucideIcons from 'lucide-react'
import {
  DRIVER_REGISTRY,
  getDriver,
  listDriversByCategory,
  buildJdbcUrl,
} from '../../data/etlDriverRegistry'
import {
  listConnections,
  saveConnection,
  updateConnection,
  deleteConnection,
} from '../../data/etlFileService'
import './ConnectionManagerModal.css'

// ---------------------------------------------------------------------------
// Field visibility check — handles visibleWhen conditions
// ---------------------------------------------------------------------------
function isFieldVisible(field, formValues) {
  if (!field.visibleWhen) return true
  return Object.entries(field.visibleWhen).every(([key, expected]) => {
    const actual = formValues[key]
    if (Array.isArray(expected)) return expected.includes(actual)
    return actual === expected
  })
}

// ---------------------------------------------------------------------------
// Connection Manager Modal
// ---------------------------------------------------------------------------
function ConnectionManagerModal({
  isOpen,
  onClose,
  initialConnectionId = null,
  initialDriverId = null,
  onApply,
}) {
  // Left sidebar state
  const [savedConnections, setSavedConnections] = useState([])
  const [connectionsLoading, setConnectionsLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedSavedId, setSelectedSavedId] = useState(null)

  // Right form state
  const [connectionName, setConnectionName] = useState('')
  const [selectedDriverId, setSelectedDriverId] = useState(null)
  const [selectedVersionId, setSelectedVersionId] = useState(null)
  const [formValues, setFormValues] = useState({})
  const [advancedValues, setAdvancedValues] = useState({})
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [isDirty, setIsDirty] = useState(false)

  // Test connection state
  const [testStatus, setTestStatus] = useState(null) // null | 'testing' | 'success' | 'error'
  const [testMessage, setTestMessage] = useState('')

  // Driver categories for sidebar
  const driversByCategory = useMemo(() => listDriversByCategory(), [])

  // Load saved connections
  const loadConnections = useCallback(async () => {
    setConnectionsLoading(true)
    try {
      const all = await listConnections()
      setSavedConnections(all)
    } catch {
      setSavedConnections([])
    } finally {
      setConnectionsLoading(false)
    }
  }, [])

  // Initialize on open
  useEffect(() => {
    if (!isOpen) return
    loadConnections()

    if (initialConnectionId) {
      // Will select after connections load
      setSelectedSavedId(initialConnectionId)
    } else if (initialDriverId && DRIVER_REGISTRY[initialDriverId]) {
      selectDriver(initialDriverId)
      setSelectedSavedId(null)
    } else {
      // Default to first driver
      selectDriver('postgresql')
      setSelectedSavedId(null)
    }
    setTestStatus(null)
    setTestMessage('')
    setIsDirty(false)
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  // When connections load and we have an initial selection, apply it
  useEffect(() => {
    if (selectedSavedId && savedConnections.length > 0) {
      const conn = savedConnections.find(c => c.id === selectedSavedId)
      if (conn) loadConnectionProfile(conn)
    }
  }, [savedConnections, selectedSavedId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Select a driver and reset form to its defaults
  const selectDriver = useCallback((driverId) => {
    const driver = getDriver(driverId)
    if (!driver) return
    setSelectedDriverId(driverId)
    setSelectedVersionId(driver.defaultVersion)

    const defaults = {}
    driver.connectionFields.forEach(f => { defaults[f.key] = f.default ?? '' })
    setFormValues(defaults)

    const advDefaults = {}
    ;(driver.advancedFields || []).forEach(f => { advDefaults[f.key] = f.default ?? '' })
    setAdvancedValues(advDefaults)

    setConnectionName('')
    setSelectedSavedId(null)
    setTestStatus(null)
    setTestMessage('')
    setIsDirty(false)
  }, [])

  // Load a saved connection profile into the form
  const loadConnectionProfile = useCallback((profile) => {
    const driverId = profile.driverId || profile.connectionFields?.connection_type || 'postgresql'
    const driver = getDriver(driverId)
    if (!driver) return

    setSelectedDriverId(driverId)
    setSelectedVersionId(profile.driverVersionId || driver.defaultVersion)
    setConnectionName(profile.name || '')
    setSelectedSavedId(profile.id)

    // Build form values from profile
    const vals = {}
    driver.connectionFields.forEach(f => {
      vals[f.key] = profile.connectionFields?.[f.key] ?? f.default ?? ''
    })
    setFormValues(vals)

    const advVals = {}
    ;(driver.advancedFields || []).forEach(f => {
      advVals[f.key] = profile.advancedFields?.[f.key]
        ?? profile.connectionFields?.[f.key]
        ?? f.default ?? ''
    })
    setAdvancedValues(advVals)

    setTestStatus(profile.lastTestResult?.success ? 'success' : null)
    setTestMessage(profile.lastTestResult?.success
      ? `Last tested: ${new Date(profile.lastTestResult.testedAt).toLocaleString()}`
      : '')
    setIsDirty(false)
  }, [])

  // Update a form value
  const handleFieldChange = useCallback((key, value) => {
    setFormValues(prev => ({ ...prev, [key]: value }))
    setIsDirty(true)
    setTestStatus(null)
  }, [])

  // Update an advanced value
  const handleAdvancedChange = useCallback((key, value) => {
    setAdvancedValues(prev => ({ ...prev, [key]: value }))
    setIsDirty(true)
    setTestStatus(null)
  }, [])

  // Port auto-update when driver changes
  const handleDriverChange = useCallback((newDriverId) => {
    const oldDriver = getDriver(selectedDriverId)
    const newDriver = getDriver(newDriverId)
    if (!newDriver) return

    setSelectedDriverId(newDriverId)
    setSelectedVersionId(newDriver.defaultVersion)

    // Update port if it was at the old driver's default
    setFormValues(prev => {
      const updated = { ...prev }
      if (oldDriver && Number(prev.port) === oldDriver.defaultPort && newDriver.defaultPort) {
        updated.port = newDriver.defaultPort
      }
      // Add any new fields with defaults
      newDriver.connectionFields.forEach(f => {
        if (updated[f.key] === undefined) updated[f.key] = f.default ?? ''
      })
      return updated
    })

    // Reset advanced to new driver defaults
    const advDefaults = {}
    ;(newDriver.advancedFields || []).forEach(f => { advDefaults[f.key] = f.default ?? '' })
    setAdvancedValues(advDefaults)

    setIsDirty(true)
    setTestStatus(null)
  }, [selectedDriverId])

  // Test connection
  const handleTestConnection = useCallback(async () => {
    setTestStatus('testing')
    setTestMessage('Connecting...')

    // Simulate a test (backend endpoint not built yet — placeholder)
    // In production this calls POST /api/etl/test-connection on the BLL
    try {
      const start = Date.now()
      // Placeholder: just validate required fields are filled
      const driver = getDriver(selectedDriverId)
      const missing = (driver?.connectionFields || [])
        .filter(f => f.required && !formValues[f.key])
        .map(f => f.label)

      if (missing.length > 0) {
        setTestStatus('error')
        setTestMessage(`Missing required fields: ${missing.join(', ')}`)
        return
      }

      // Simulate latency
      await new Promise(r => setTimeout(r, 400 + Math.random() * 300))
      const elapsed = Date.now() - start

      setTestStatus('success')
      setTestMessage(`Connected (${elapsed}ms)`)
    } catch (err) {
      setTestStatus('error')
      setTestMessage(err.message || 'Connection failed')
    }
  }, [selectedDriverId, formValues])

  // Save connection (create or update)
  const handleSave = useCallback(async () => {
    const name = connectionName.trim() || `${getDriver(selectedDriverId)?.name || 'Connection'} - ${formValues.host || formValues.bucket || 'new'}`
    const profile = {
      name,
      description: '',
      driverId: selectedDriverId,
      driverVersionId: selectedVersionId,
      nodeType: `etl-source-${selectedDriverId}`, // legacy compat
      category: 'sources',
      connectionFields: { ...formValues },
      advancedFields: { ...advancedValues },
      lastTestResult: testStatus === 'success' ? {
        success: true,
        testedAt: new Date().toISOString(),
      } : null,
    }

    try {
      let result
      if (selectedSavedId) {
        result = await updateConnection(selectedSavedId, profile)
        result = { ...profile, id: selectedSavedId }
      } else {
        result = await saveConnection(profile)
      }
      await loadConnections()
      if (result?.id) setSelectedSavedId(result.id)
      setIsDirty(false)
      setConnectionName(name)
      return result
    } catch (err) {
      console.error('Failed to save connection:', err)
      return null
    }
  }, [connectionName, selectedDriverId, selectedVersionId, formValues, advancedValues, testStatus, selectedSavedId, loadConnections])

  // Delete a connection
  const handleDeleteConnection = useCallback(async (connId, e) => {
    e?.stopPropagation()
    try {
      await deleteConnection(connId)
      if (selectedSavedId === connId) {
        selectDriver(selectedDriverId || 'postgresql')
      }
      await loadConnections()
    } catch (err) {
      console.error('Failed to delete connection:', err)
    }
  }, [selectedSavedId, selectedDriverId, selectDriver, loadConnections])

  // Apply + close
  const handleOk = useCallback(async () => {
    let profile = null
    if (isDirty || !selectedSavedId) {
      profile = await handleSave()
    } else {
      profile = savedConnections.find(c => c.id === selectedSavedId)
    }

    if (onApply && profile) {
      onApply({
        id: profile.id || selectedSavedId,
        name: connectionName || profile.name,
        driverId: selectedDriverId,
        driverVersionId: selectedVersionId,
        connectionFields: { ...formValues },
        advancedFields: { ...advancedValues },
      })
    }
    onClose()
  }, [isDirty, selectedSavedId, savedConnections, connectionName, selectedDriverId, selectedVersionId, formValues, advancedValues, handleSave, onApply, onClose])

  // Current driver
  const currentDriver = getDriver(selectedDriverId)
  const currentVersion = currentDriver?.versions.find(v => v.id === selectedVersionId) || null

  // Generated URL
  const jdbcUrl = buildJdbcUrl(selectedDriverId, formValues)

  // Filtered saved connections
  const filteredConnections = searchQuery
    ? savedConnections.filter(c =>
        c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.driverId?.toLowerCase().includes(searchQuery.toLowerCase()))
    : savedConnections

  // Render a form field
  const renderField = (field, value, onChange) => {
    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value || ''}
            onChange={e => onChange(field.key, e.target.value)}
            placeholder={field.description || ''}
          />
        )
      case 'number':
        return (
          <input
            type="number"
            value={value ?? ''}
            onChange={e => onChange(field.key, e.target.value === '' ? '' : Number(e.target.value))}
          />
        )
      case 'password':
        return (
          <input
            type="password"
            value={value || ''}
            onChange={e => onChange(field.key, e.target.value)}
          />
        )
      case 'select':
        return (
          <select
            value={value || ''}
            onChange={e => onChange(field.key, e.target.value)}
          >
            {(field.options || []).map(opt => {
              const optVal = typeof opt === 'object' ? opt.value : opt
              const optLabel = typeof opt === 'object' ? opt.label : opt
              return <option key={optVal} value={optVal}>{optLabel}</option>
            })}
          </select>
        )
      case 'checkbox':
        return (
          <label className="cm-checkbox">
            <input
              type="checkbox"
              checked={!!value}
              onChange={e => onChange(field.key, e.target.checked)}
            />
            <span>{field.label}</span>
          </label>
        )
      default:
        return (
          <input
            type="text"
            value={value || ''}
            onChange={e => onChange(field.key, e.target.value)}
          />
        )
    }
  }

  if (!isOpen) return null

  return (
    <div className="cm-overlay" onClick={onClose}>
      <div className="cm-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="cm-header">
          <h2>Connection Manager</h2>
          <button className="cm-close" onClick={onClose}>
            <LucideIcons.X size={18} />
          </button>
        </div>

        {/* Body: sidebar + form */}
        <div className="cm-body">
          {/* Left Sidebar */}
          <div className="cm-sidebar">
            {/* Actions */}
            <div className="cm-sidebar-actions">
              <button
                className="cm-new-btn"
                onClick={() => selectDriver(selectedDriverId || 'postgresql')}
                title="New connection"
              >
                <LucideIcons.Plus size={14} />
                New
              </button>
              <div className="cm-search">
                <LucideIcons.Search size={12} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                />
              </div>
            </div>

            <div className="cm-sidebar-scroll">
              {/* Saved Connections */}
              {filteredConnections.length > 0 && (
                <div className="cm-sidebar-group">
                  <div className="cm-sidebar-group-label">Saved Connections</div>
                  {filteredConnections.map(conn => {
                    const drv = getDriver(conn.driverId || conn.connectionFields?.connection_type)
                    const Icon = LucideIcons[drv?.icon || 'Database'] || LucideIcons.Database
                    return (
                      <div
                        key={conn.id}
                        className={`cm-sidebar-item ${selectedSavedId === conn.id ? 'active' : ''}`}
                        onClick={() => loadConnectionProfile(conn)}
                      >
                        <Icon size={14} style={{ flexShrink: 0, color: '#6cb6ff' }} />
                        <span className="cm-sidebar-item-name">{conn.name}</span>
                        <span className="cm-sidebar-item-badge">{drv?.name || ''}</span>
                        <button
                          className="cm-sidebar-item-delete"
                          onClick={e => handleDeleteConnection(conn.id, e)}
                          title="Delete"
                        >
                          <LucideIcons.X size={10} />
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Driver Tree */}
              {Object.entries(driversByCategory).map(([category, drivers]) => (
                <div key={category} className="cm-sidebar-group">
                  <div className="cm-sidebar-group-label">{category}</div>
                  {drivers.map(driver => {
                    const Icon = LucideIcons[driver.icon] || LucideIcons.Database
                    return (
                      <div
                        key={driver.id}
                        className={`cm-sidebar-item ${selectedDriverId === driver.id && !selectedSavedId ? 'active' : ''}`}
                        onClick={() => selectDriver(driver.id)}
                      >
                        <Icon size={14} style={{ flexShrink: 0 }} />
                        <span className="cm-sidebar-item-name">{driver.name}</span>
                      </div>
                    )
                  })}
                </div>
              ))}
            </div>
          </div>

          {/* Right Form */}
          <div className="cm-form">
            {currentDriver ? (
              <div className="cm-form-scroll">
                {/* Connection Name */}
                <div className="cm-form-section">
                  <div className="cm-form-field">
                    <label>Connection Name</label>
                    <input
                      type="text"
                      value={connectionName}
                      onChange={e => { setConnectionName(e.target.value); setIsDirty(true) }}
                      placeholder={`${currentDriver.name} connection`}
                    />
                  </div>
                </div>

                {/* Driver + Version */}
                <div className="cm-form-section">
                  <div className="cm-form-section-label">Driver</div>
                  <div className="cm-form-row">
                    <div className="cm-form-field cm-form-field-half">
                      <label>Database</label>
                      <select
                        value={selectedDriverId || ''}
                        onChange={e => handleDriverChange(e.target.value)}
                      >
                        {Object.values(DRIVER_REGISTRY).map(d => (
                          <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="cm-form-field cm-form-field-half">
                      <label>Version</label>
                      <select
                        value={selectedVersionId || ''}
                        onChange={e => { setSelectedVersionId(e.target.value); setIsDirty(true) }}
                      >
                        {currentDriver.versions.map(v => (
                          <option key={v.id} value={v.id}>{v.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {currentVersion?.notes && (
                    <div className="cm-form-hint">{currentVersion.notes}</div>
                  )}
                  {currentVersion?.jarName && (
                    <div className="cm-driver-info">
                      <span className="cm-driver-jar">
                        <LucideIcons.Package size={12} />
                        {currentVersion.jarName}
                      </span>
                      {currentVersion.downloadUrl && (
                        <a
                          href={currentVersion.downloadUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="cm-driver-download"
                        >
                          <LucideIcons.Download size={12} />
                          Download
                        </a>
                      )}
                      {!currentVersion.downloadUrl && (
                        <span className="cm-driver-proprietary">
                          <LucideIcons.AlertTriangle size={12} />
                          Customer-provided
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Connection Fields */}
                <div className="cm-form-section">
                  <div className="cm-form-section-label">Connection</div>
                  {currentDriver.connectionFields
                    .filter(f => f.type !== 'checkbox' && isFieldVisible(f, formValues))
                    .map(field => (
                      <div key={field.key} className="cm-form-field">
                        <label>
                          {field.label}
                          {field.required && <span className="cm-required">*</span>}
                        </label>
                        {field.description && (
                          <span className="cm-field-desc">{field.description}</span>
                        )}
                        {renderField(field, formValues[field.key], handleFieldChange)}
                      </div>
                    ))}
                  {currentDriver.connectionFields
                    .filter(f => f.type === 'checkbox' && isFieldVisible(f, formValues))
                    .map(field => (
                      <div key={field.key} className="cm-form-field">
                        {renderField(field, formValues[field.key], handleFieldChange)}
                      </div>
                    ))}
                </div>

                {/* Advanced Fields */}
                {currentDriver.advancedFields?.length > 0 && (
                  <div className="cm-form-section">
                    <div
                      className="cm-form-section-label cm-clickable"
                      onClick={() => setShowAdvanced(!showAdvanced)}
                    >
                      Advanced
                      {showAdvanced
                        ? <LucideIcons.ChevronUp size={14} />
                        : <LucideIcons.ChevronDown size={14} />}
                    </div>
                    {showAdvanced && (
                      <>
                        {currentDriver.advancedFields
                          .filter(f => isFieldVisible(f, { ...formValues, ...advancedValues }))
                          .map(field => (
                            <div key={field.key} className="cm-form-field">
                              {field.type !== 'checkbox' && (
                                <label>{field.label}</label>
                              )}
                              {field.description && (
                                <span className="cm-field-desc">{field.description}</span>
                              )}
                              {renderField(field, advancedValues[field.key], handleAdvancedChange)}
                            </div>
                          ))}
                      </>
                    )}
                  </div>
                )}

                {/* JDBC URL */}
                {jdbcUrl && (
                  <div className="cm-form-section">
                    <div className="cm-form-section-label">URL</div>
                    <div className="cm-jdbc-url">
                      <code>{jdbcUrl}</code>
                    </div>
                  </div>
                )}

                {/* Test Connection */}
                <div className="cm-form-section cm-test-section">
                  <button
                    className="cm-test-btn"
                    onClick={handleTestConnection}
                    disabled={testStatus === 'testing'}
                  >
                    {testStatus === 'testing' ? (
                      <><LucideIcons.Loader2 size={14} className="cm-spin" /> Testing...</>
                    ) : (
                      <><LucideIcons.Zap size={14} /> Test Connection</>
                    )}
                  </button>
                  {testStatus && testStatus !== 'testing' && (
                    <div className={`cm-test-result ${testStatus}`}>
                      {testStatus === 'success' ? (
                        <><LucideIcons.CheckCircle size={14} /> {testMessage}</>
                      ) : (
                        <><LucideIcons.XCircle size={14} /> {testMessage}</>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="cm-form-empty">
                <LucideIcons.Database size={32} />
                <p>Select a driver or saved connection</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="cm-footer">
          <button className="cm-btn cm-btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <div className="cm-footer-spacer" />
          <button
            className="cm-btn cm-btn-secondary"
            onClick={handleSave}
            disabled={!currentDriver}
          >
            <LucideIcons.Save size={14} />
            {selectedSavedId ? 'Update' : 'Save'}
          </button>
          <button
            className="cm-btn cm-btn-primary"
            onClick={handleOk}
            disabled={!currentDriver}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  )
}

export default ConnectionManagerModal
